import { Engagement, EngagementStatus, EngagementStepStatus } from '@aiwyn/api';
import { AuthService, AuthStateAdaptorService } from '@aiwyn/auth';
import { AssetUriService, ConfigService } from '@aiwyn/config';
import { ButtonColor, DATA_SELECTOR_INJECTION_TOKEN, DataSelectorDirective, fadeInOut, Loadable, LoadableStatus, Logger, mapToLoadable, SharedModule, SnackbarService, SnackbarType, UseFunctionPipe } from '@aiwyn/shared';
import { CommonModule } from '@angular/common';
import { Component, inject, input, output, signal, viewChild } from '@angular/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { ROUTE_TOKEN_ENGAGEMENTS_BASE, ROUTE_TOKEN_MAIN_BASE } from '../../../../routing-constants';
import { DestinationEngagementStepType, EngagementStepDisplay } from '../../models';
import { EngagementsState } from '../../state';
import { EngagementReassignmentFormComponent } from '../engagement-reassignment-form/engagement-reassignment-form.component';
import { EngagementStepListComponent } from '../engagement-step-list/engagement-step-list.component';
import { ENGAGEMENT_LANDING_BASE_INFO, EngagementLandingAcknowledgement, EngagementLandingUtil, EngagementLandingView } from './engagement-landing';

type EngagementLandingButton = {
  color?: ButtonColor;
  hide?: boolean;
  icon?: string;
  label: string;
  testId: string;
  onClick: () => void;
};

interface EngagementLandingPayload {
  acknowledgement?: string;
  successMessage?: string;
  buttons?: EngagementLandingButton[];
  descriptions: string[];
  engagement: Engagement;
  engagementSteps?: EngagementStepDisplay[];
  title?: string;
  view: EngagementLandingView;
}

@Component({
  selector: 'app-engagement-landing',
  templateUrl: './engagement-landing.component.html',
  styleUrls: ['./engagement-landing.component.scss'],
  animations: [fadeInOut],
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinner,
    SharedModule,
    DataSelectorDirective,
    EngagementReassignmentFormComponent,
    EngagementStepListComponent,
  ],
  providers: [
    UseFunctionPipe,
    {
      provide: DATA_SELECTOR_INJECTION_TOKEN,
      useValue: 'engagement-landing',
    },
  ],
})
export class EngagementLandingComponent {
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _assetUriService = inject(AssetUriService);
  private readonly _authService = inject(AuthService);
  private readonly _authStateAdaptor = inject(AuthStateAdaptorService);
  private readonly _configService = inject(ConfigService);
  private readonly _logger = inject(Logger);
  private readonly _router = inject(Router);
  private readonly _snackbarService = inject(SnackbarService);
  private readonly _store = inject(Store);
  private readonly _useFunctionPipe = inject(UseFunctionPipe);
  
  readonly isUsingSecureLink = input<boolean>();
  readonly secureLinkReassignmentView = input<boolean>();
  readonly advance = output<DestinationEngagementStepType | void>();

  readonly customerAssetsUrl = this._assetUriService.customerAssetUrl;
  readonly dataSelector = inject(DATA_SELECTOR_INJECTION_TOKEN);
  readonly EngagementStatus = EngagementStatus;
  readonly EngagementStepStatus = EngagementStepStatus;
  readonly EngagementLandingView = EngagementLandingView;
  readonly LoadableStatus = LoadableStatus;
  readonly ROUTE_TOKEN_MAIN_BASE = ROUTE_TOKEN_MAIN_BASE;
  readonly ROUTE_TOKEN_ENGAGEMENTS_BASE = ROUTE_TOKEN_ENGAGEMENTS_BASE;

  readonly engagementReassignmentFormRef = viewChild<EngagementReassignmentFormComponent>(EngagementReassignmentFormComponent);

  readonly loading = signal<boolean>(false);
  readonly saving = signal<boolean>(false);

  readonly enabledReassignment$ = this._activatedRoute.queryParams.pipe(
    map(data => data?.['enabledReassignment'] === 'true')
  );
  
  private readonly _engagement$ = new ReplaySubject<Engagement>(1);
  readonly engagement$ = this._store.select(EngagementsState.getSelectedEngagementWithDate).pipe(
    tap((engagement) => engagement && this._engagement$.next(engagement))
  );
  readonly engagementSteps$ = this._store.select(EngagementsState.getSelectedEngagementVisibleStepsDisplay);

  readonly loadableContent$: Observable<Loadable<EngagementLandingPayload>> = combineLatest([this.enabledReassignment$, this._engagement$]).pipe(
    withLatestFrom(this._store.select(EngagementsState.isSelectedEngagementReassignable)),
    switchMap(([[enabledReassignment, engagement], isSelectedEngagementReassignable]) => {
      if (engagement.justReassignedTo) {
        return of(this.buildReassignedPayload(engagement));
      }

      // Handle reassignment view
      const showReassignmentView = this.secureLinkReassignmentView() || enabledReassignment;
      if (this._configService.engagementReassignmentEnabled && isSelectedEngagementReassignable && showReassignmentView) {
        return of(this.buildReassignmentPayload(engagement));
      }

      // Handle non-editable engagement statuses
      if (engagement.status === EngagementStatus.UNDER_REVISION) {
        return of(this.buildUnderRevisionPayload(engagement));
      }

      if (engagement.status === EngagementStatus.CANCELED) {
        return of(this.buildCanceledPayload(engagement));
      }
      
      // Handle normal engagement flow with steps
      return this.engagementSteps$.pipe(
        withLatestFrom(this._authStateAdaptor.getUserContactId()),
        map(([engagementSteps, userContactId]) => 
          this.buildStepsPayload(engagement, engagementSteps, userContactId)
        )
      );
    }),
    mapToLoadable<EngagementLandingPayload>()
  );

  private buildReassignmentPayload(engagement: Engagement): EngagementLandingPayload {
    const baseInfo = this._useFunctionPipe.transform(ENGAGEMENT_LANDING_BASE_INFO[EngagementLandingView.REASSIGNMENT], engagement);
    return {
      engagement,
      title: baseInfo.title,
      descriptions: [baseInfo.description],
      acknowledgement: EngagementLandingAcknowledgement.REASSIGNMENT,
      view: EngagementLandingView.REASSIGNMENT,
      buttons: [
        this.viewEngagementButton(),
        this.confirmReassignmentButton(engagement),
      ],
    };
  }

  private buildUnderRevisionPayload(engagement: Engagement): EngagementLandingPayload {
    const baseInfo = this._useFunctionPipe.transform(ENGAGEMENT_LANDING_BASE_INFO[EngagementStatus.UNDER_REVISION], engagement);
    return {
      engagement,
      title: baseInfo.title,
      descriptions: [baseInfo.description],
      view: EngagementLandingView.STEPS,
      buttons: [this.backToEngagementsButton()],
    };
  }

  private buildCanceledPayload(engagement: Engagement): EngagementLandingPayload {
    const baseInfo = this._useFunctionPipe.transform(ENGAGEMENT_LANDING_BASE_INFO[EngagementStatus.CANCELED], engagement);
    return {
      engagement,
      title: baseInfo.title,
      descriptions: [baseInfo.description],
      view: EngagementLandingView.STEPS,  
      buttons: [this.backToEngagementsButton()],
    };
  }

  private buildReassignedPayload(engagement: Engagement): EngagementLandingPayload {
    const successDescription = `The engagement has been reassigned to <strong>${engagement.justReassignedTo}</strong> for review and completion. You can close this window or return to your engagements list.`;
    
    const buttons: EngagementLandingButton[] = [];

    if (this.isUsingSecureLink()) {
      buttons.push(this.signInButton());
    } else {
      buttons.push(this.backToEngagementsButton(true));
    }

    return {
      buttons,
      engagement,
      descriptions: [successDescription],
      successMessage: 'Reassignment complete!',
      view: EngagementLandingView.STEPS,
    };
  }

  private buildStepsPayload(
    engagement: Engagement, 
    engagementSteps: EngagementStepDisplay[], 
    userContactId: number
  ): EngagementLandingPayload {
    const completedSteps = engagementSteps.filter(step => step.status === EngagementStepStatus.COMPLETE);
    const completedCount = completedSteps.length;
    const totalCount = engagementSteps.length;
    const progressText = `${completedCount} of ${totalCount} completed`;

    const buttons: EngagementLandingButton[] = [this.backToEngagementsButton()];

    // Set title and descriptions based on engagement status
    let title: string;
    let descriptions: string[];
    
    if (engagement.status === EngagementStatus.EXECUTED) {
      const baseInfo = this._useFunctionPipe.transform(ENGAGEMENT_LANDING_BASE_INFO[EngagementStatus.EXECUTED], engagement);
      title = baseInfo.title;
      descriptions = [baseInfo.description, progressText];
    } else {
      const remainingStepsInfo = EngagementLandingUtil.info({ engagement, engagementSteps, userContactId });
      title = remainingStepsInfo.title;
      descriptions = [remainingStepsInfo.description, progressText];
    }

    // Add advance button if engagement is editable
    let acknowledgement: string | undefined;
    if (this.isEngagementEditable(engagement.status)) {
      const advanceButtonLabel = this.getAdvanceButtonLabel(completedCount, totalCount);
      acknowledgement = EngagementLandingAcknowledgement.STEPS;
      buttons.push(this.advanceButton(advanceButtonLabel));
    }

    return { engagement, title, descriptions, acknowledgement, view: EngagementLandingView.STEPS, buttons, engagementSteps };
  }

  private isEngagementEditable(status: EngagementStatus): boolean {
    return ![EngagementStatus.UNDER_REVISION, EngagementStatus.CANCELED].includes(status);
  }

  private getAdvanceButtonLabel(completedCount: number, totalCount: number): string {
    if (completedCount === totalCount) {
      return 'Review';
    }
    if (completedCount === 0) {
      return 'Get Started';
    }
    return 'Resume';
  }

  // Button factory methods
  private backToEngagementsButton(forceRefresh: boolean = false): EngagementLandingButton {
    return { 
      label: 'Back to Engagements', 
      icon: 'arrow-left', 
      hide: this.isUsingSecureLink(),
      testId: 'back-to-engagements-button',
      onClick: () => this._router.navigateByUrl(`${ROUTE_TOKEN_MAIN_BASE}/${ROUTE_TOKEN_ENGAGEMENTS_BASE}`, { state: { forceRefresh } }),
    };
  }

  private viewEngagementButton(): EngagementLandingButton {
    return {
      label: 'View Engagement',
      testId: 'view-engagement-button',
      onClick: () => this.advance.emit(),
    };
  }

  private signInButton(): EngagementLandingButton {
    return {
      label: 'Sign in',
      icon: 'log-in',
      testId: 'sign-in-button',
      onClick: () => {
        this._authService.signOut().pipe(take(1)).subscribe();
      },
    };
  }

  private confirmReassignmentButton(engagement: Engagement): EngagementLandingButton {
    return {
      label: 'Confirm Reassignment',
      color: 'primary',
      testId: 'confirm-reassignment-button',
      onClick: () => {
        if (this.engagementReassignmentFormRef()?.submit) {
          this.saving.set(true);
          this.engagementReassignmentFormRef().submit().pipe(
            tap({
              next: (response) => {
                this.saving.set(false);

                if (response) {
                  const { newContactFirstName, newContactLastName } = response;
                  this._engagement$.next({ ...engagement, reassigned: true, justReassignedTo: `${newContactFirstName} ${newContactLastName}` });
                }
              },
              error: (error) => {
                this.saving.set(false);
                this._logger.error('ENGAGEMENT : EngagementLandingComponent : confirmReassignmentButton : ', error);
                this._snackbarService.open('Engagement reassignment failed. Please try again.', SnackbarType.ERROR);
              },
            }),
          ).subscribe();
        }
      },
    };
  }

  private advanceButton(label: string): EngagementLandingButton {
    return { label, color: 'primary', testId: 'advance-button', onClick: () => this.advance.emit() };
  }
}
